// ApplicationControlProfiles.tsx
import { useState, useCallback, useEffect } from "react";
import {
    AppWindow, RefreshCw, Loader2, Search,
    ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
    ArrowUp, ArrowDown, Layers, Hash, ArrowLeft, Info, Shield,
} from "lucide-react";
import { showError } from "../../../../../utils/toast";
import baseapi from "../../../../../services/baseapi";

// ── Types ─────────────────────────────────────────────────────────────────────
interface AppEntry {
    id: number;
    log: string;
    risk: any[];
    action: string;
    shaper: string;
    vendor: string;
    behavior: string;
    category: { id: number; q_origin_key: number }[];
    exclusion: any[];
    protocols: string;
    "rate-mode": string;
    "log-packet": string;
    parameters: any[];
    popularity: string;
    quarantine: string;
    "rate-count": number;
    "rate-track": string;
    technology: string;
    application: { id: number; q_origin_key: number }[];
    "session-ttl": number;
    "q_origin_key": number;
    "per-ip-shaper": string;
    "rate-duration": number;
    "quarantine-log": string;
    "shaper-reverse": string;
    "quarantine-expiry": string;
    status: string;
}

interface AppProfile {
    name: string;
    comment: string;
    entries: AppEntry[];
    options: string;
    "extended-log": string;
    "app-replacemsg": string;
    "p2p-block-list": string;
    "replacemsg-group": string;
    "deep-app-inspection": string;
    "other-application-log": string;
    "unknown-application-log": string;
    "enforce-default-app-port": string;
    "other-application-action": string;
    "unknown-application-action": string;
    "force-inclusion-ssl-di-sigs": string;
    "control-default-network-services": string;
}

// ── Value Chip ────────────────────────────────────────────────────────────────
const ValChip = ({ val }: { val: string | number | undefined }) => {
    const str = String(val ?? "");
    if (!str || str === "none" || str === "") return <span className="text-slate-400 dark:text-gray-500 text-[9px]">—</span>;
    const lower = str.toLowerCase();
    const style =
        lower === "enable" || lower === "block"
            ? "bg-green-500/10 border-green-500/20 text-green-600 dark:text-green-400"
            : lower === "disable"
                ? "bg-slate-100 dark:bg-gray-700/40 border-slate-200 dark:border-gray-600/30 text-slate-500 dark:text-gray-400"
                : lower === "pass"
                    ? "bg-cyan-500/10 border-cyan-500/20 text-cyan-600 dark:text-cyan-400"
                    : lower === "all"
                        ? "bg-slate-100 dark:bg-gray-600/20 border-slate-200 dark:border-gray-600/20 text-slate-600 dark:text-gray-300"
                        : lower === "continuous"
                            ? "bg-purple-500/10 border-purple-500/20 text-purple-600 dark:text-purple-400"
                            : "bg-indigo-500/10 border-indigo-500/20 text-indigo-600 dark:text-indigo-400";

    return (
        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[8px] font-bold border uppercase whitespace-nowrap ${style}`}>
            {str}
        </span>
    );
};

const PopularityChips = ({ val }: { val: string }) => {
    if (!val) return <span className="text-slate-400 dark:text-gray-500 text-[9px]">—</span>;
    return (
        <div className="flex gap-0.5 flex-wrap">
            {val.split(" ").filter(Boolean).map(p => (
                <span key={p} className="w-4 h-4 flex items-center justify-center rounded text-[8px] font-black bg-indigo-500/10 border border-indigo-500/20 text-indigo-600 dark:text-indigo-400">
                    {p}
                </span>
            ))}
        </div>
    );
};

// ── Profile Table Columns ─────────────────────────────────────────────────────
const PROFILE_COLS: {
    key: string;
    label: string;
    width: string;
    render: (p: AppProfile) => React.ReactNode;
}[] = [
        {
            key: "name", label: "Profile Name", width: "min-w-[160px]",
            render: p => <p className="text-[11px] font-black text-slate-900 dark:text-white">{p.name.replace(/_/g, " ")}</p>,
        },
        {
            key: "_entryCount", label: "Rules", width: "w-14",
            render: p => (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-black bg-blue-500/10 border border-blue-500/20 text-blue-600 dark:text-blue-400">
                    <Layers size={9} />{p.entries.length}
                </span>
            ),
        },
        { key: "options", label: "Options", width: "w-24", render: p => <ValChip val={p.options || "—"} /> },
        { key: "deep-app-inspection", label: "Deep Inspect", width: "w-24", render: p => <ValChip val={p["deep-app-inspection"]} /> },
        { key: "enforce-default-app-port", label: "Enforce Port", width: "w-24", render: p => <ValChip val={p["enforce-default-app-port"]} /> },
        { key: "app-replacemsg", label: "Replace Msg", width: "w-24", render: p => <ValChip val={p["app-replacemsg"]} /> },
        { key: "other-application-action", label: "Other App Action", width: "w-28", render: p => <ValChip val={p["other-application-action"]} /> },
        { key: "other-application-log", label: "Other App Log", width: "w-24", render: p => <ValChip val={p["other-application-log"]} /> },
        { key: "unknown-application-action", label: "Unknown Action", width: "w-28", render: p => <ValChip val={p["unknown-application-action"]} /> },
        { key: "unknown-application-log", label: "Unknown Log", width: "w-24", render: p => <ValChip val={p["unknown-application-log"]} /> },
        { key: "extended-log", label: "Ext Log", width: "w-16", render: p => <ValChip val={p["extended-log"]} /> },
        { key: "force-inclusion-ssl-di-sigs", label: "SSL DI Sigs", width: "w-20", render: p => <ValChip val={p["force-inclusion-ssl-di-sigs"]} /> },
        { key: "control-default-network-services", label: "Ctrl Net Svc", width: "w-24", render: p => <ValChip val={p["control-default-network-services"]} /> },
        {
            key: "comment", label: "Comment", width: "min-w-[140px]",
            render: p => <p className="text-[10px] text-slate-500 dark:text-gray-400 truncate max-w-[180px]" title={p.comment}>{p.comment || "—"}</p>,
        },
    ];

// ── Detail View ───────────────────────────────────────────────────────────────
function ProfileDetail({ profile, onBack }: { profile: AppProfile; onBack: () => void }) {
    return (
        <div className="space-y-3 animate-fade-in">

            {/* Header */}
            <div className="flex items-center gap-3 bg-white dark:bg-gray-800/40 border border-slate-200 dark:border-gray-700 p-2.5 rounded-xl shadow-sm dark:shadow-none">
                <button
                    onClick={onBack}
                    className="p-1.5 bg-violet-500/10 border border-violet-500/30 rounded-md text-violet-600 dark:text-violet-400 hover:text-white hover:bg-violet-600 transition-all group shrink-0"
                >
                    <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
                </button>
                <div className="bg-violet-500/10 p-1.5 rounded-md border border-violet-500/20 shrink-0">
                    <AppWindow className="w-4 h-4 text-violet-600 dark:text-violet-400" />
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="text-sm font-bold text-slate-900 dark:text-white">{profile.name}</h2>
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-black bg-blue-500/10 border border-blue-500/20 text-blue-600 dark:text-blue-400">
                        <Layers size={9} />{profile.entries.length} rules
                    </span>
                </div>
                {profile.comment && (
                    <p className="text-[10px] text-slate-500 dark:text-gray-400 ml-2">{profile.comment}</p>
                )}
            </div>

            {/* Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">

                {/* Profile Flags */}
                <div className="bg-white dark:bg-gray-800/40 border border-slate-200 dark:border-gray-700 p-3 rounded-lg shadow-sm dark:shadow-none">
                    <div className="flex items-center gap-1.5 mb-2 border-b border-slate-100 dark:border-gray-700 pb-1.5">
                        <Info className="w-3.5 h-3.5 text-violet-600 dark:text-violet-400" />
                        <h4 className="text-[10px] font-black text-violet-600 dark:text-violet-400 uppercase tracking-widest">Profile Settings</h4>
                    </div>
                    <div className="space-y-0.5">
                        {[
                            { label: "Options", val: profile.options },
                            { label: "Deep Inspect", val: profile["deep-app-inspection"] },
                            { label: "Enforce Port", val: profile["enforce-default-app-port"] },
                            { label: "Replace Msg", val: profile["app-replacemsg"] },
                            { label: "Extended Log", val: profile["extended-log"] },
                            { label: "SSL DI Sigs", val: profile["force-inclusion-ssl-di-sigs"] },
                            { label: "Ctrl Net Svc", val: profile["control-default-network-services"] },
                            { label: "Other App Action", val: profile["other-application-action"] },
                            { label: "Other App Log", val: profile["other-application-log"] },
                            { label: "Unknown Action", val: profile["unknown-application-action"] },
                            { label: "Unknown Log", val: profile["unknown-application-log"] },
                        ].map(({ label, val }) => (
                            <div key={label} className="flex justify-between items-center py-1 border-b border-slate-100 dark:border-gray-700/40 last:border-0 hover:bg-slate-50 dark:hover:bg-gray-700/20 px-1 -mx-1 rounded transition-colors">
                                <span className="text-[9px] font-bold text-slate-500 dark:text-gray-400 uppercase tracking-widest">{label}</span>
                                <ValChip val={val} />
                            </div>
                        ))}
                    </div>
                </div>

                {/* Entries */}
                <div className="md:col-span-1 lg:col-span-2 space-y-3">
                    <div className="flex items-center gap-2">
                        <Shield className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
                        <h4 className="text-[10px] font-black text-blue-600 dark:text-blue-400 uppercase tracking-widest">Rules / Entries</h4>
                        <span className="px-1.5 py-0.5 text-[8px] font-bold bg-blue-500/10 border border-blue-500/20 text-blue-600 dark:text-blue-400 rounded">
                            {profile.entries.length}
                        </span>
                    </div>

                    {profile.entries.length === 0 ? (
                        <div className="flex flex-col items-center gap-2 py-10 bg-white dark:bg-gray-800/40 border border-slate-200 dark:border-gray-700 rounded-lg opacity-40 shadow-sm dark:shadow-none">
                            <Layers size={20} className="text-slate-400 dark:text-gray-400" />
                            <span className="text-[10px] text-slate-500 dark:text-gray-400 uppercase tracking-widest font-bold">No entries</span>
                        </div>
                    ) : (
                        <div className="space-y-3 max-h-[70vh] overflow-y-auto custom-scroll pr-1">
                            {profile.entries.map(entry => {
                                const isBlock = entry.action === "block";
                                const isPass = entry.action === "pass";
                                const borderColor = isBlock ? "border-green-200 dark:border-green-500/30" : isPass ? "border-cyan-200 dark:border-cyan-500/30" : "border-slate-200 dark:border-gray-600/30";
                                const accentColor = isBlock ? "text-green-600 dark:text-green-400" : isPass ? "text-cyan-600 dark:text-cyan-400" : "text-slate-700 dark:text-gray-200";
                                const bgColor = isBlock ? "bg-green-50/50 dark:bg-green-500/5" : isPass ? "bg-cyan-50/50 dark:bg-cyan-500/5" : "bg-slate-50/50 dark:bg-gray-800/40";

                                return (
                                    <div key={entry.id} className={`border rounded-xl overflow-hidden ${borderColor} bg-white dark:bg-gray-800 shadow-sm dark:shadow-none`}>
                                        {/* Entry Header */}
                                        <div className={`flex items-center gap-2 px-3 py-2 ${bgColor}`}>
                                            <span className={`text-[10px] font-black ${accentColor}`}>Rule #{entry.id}</span>
                                            <ValChip val={entry.action} />
                                            <ValChip val={entry.log === "enable" ? "log" : "no-log"} />
                                        </div>

                                        {/* Entry Fields Grid */}
                                        <div className="px-3 py-2.5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-2.5 bg-slate-50/30 dark:bg-gray-900/30">
                                            {[
                                                { label: "Protocols", val: entry.protocols },
                                                { label: "Technology", val: entry.technology },
                                                { label: "Vendor", val: entry.vendor },
                                                { label: "Behavior", val: entry.behavior },
                                                { label: "Quarantine", val: entry.quarantine },
                                                { label: "Quar Log", val: entry["quarantine-log"] },
                                                { label: "Quar Expiry", val: entry["quarantine-expiry"] },
                                                { label: "Rate Mode", val: entry["rate-mode"] },
                                                { label: "Rate Dur", val: String(entry["rate-duration"]) + "s" },
                                                { label: "Log Packet", val: entry["log-packet"] },
                                            ].map(({ label, val }) => (
                                                <div key={label} className="flex flex-col gap-0.5">
                                                    <span className="text-[8px] font-bold text-slate-500 dark:text-gray-400 uppercase tracking-widest">{label}</span>
                                                    <ValChip val={val} />
                                                </div>
                                            ))}
                                            <div className="col-span-2 flex flex-col gap-0.5">
                                                <span className="text-[8px] font-bold text-slate-500 dark:text-gray-400 uppercase tracking-widest">Popularity</span>
                                                <PopularityChips val={entry.popularity} />
                                            </div>
                                        </div>

                                        {/* App IDs */}
                                        {entry.application.length > 0 && (
                                            <div className="px-3 py-2 border-t border-slate-100 dark:border-gray-700/40 bg-slate-50/50 dark:bg-gray-800/20">
                                                <p className="text-[8px] font-bold text-slate-500 dark:text-gray-400 uppercase tracking-widest mb-1.5">
                                                    App IDs ({entry.application.length})
                                                </p>
                                                <div className="flex flex-wrap gap-1">
                                                    {entry.application.map(a => (
                                                        <span key={a.id} className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[8px] font-mono font-bold bg-slate-100 dark:bg-gray-700/50 border border-slate-200 dark:border-gray-600/30 text-slate-700 dark:text-gray-300">
                                                            <Hash size={7} />{a.id}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* Categories */}
                                        {entry.category.length > 0 && (
                                            <div className="px-3 py-2 border-t border-slate-100 dark:border-gray-700/40 bg-slate-50/50 dark:bg-gray-800/20">
                                                <p className="text-[8px] font-bold text-slate-500 dark:text-gray-400 uppercase tracking-widest mb-1.5">
                                                    Categories ({entry.category.length})
                                                </p>
                                                <div className="flex flex-wrap gap-1">
                                                    {entry.category.map(c => (
                                                        <span key={c.id} className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[8px] font-mono font-bold bg-orange-500/10 border border-orange-500/20 text-orange-600 dark:text-orange-400">
                                                            <Hash size={7} />{c.id}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

// ── Main Component ────────────────────────────────────────────────────────────
const PAGE_SIZE_OPTIONS = [10, 20, 50];

export default function ApplicationControlProfiles({ asset_id, client_id }: { asset_id: any, client_id: string }) {
    const [profiles, setProfiles] = useState<AppProfile[]>([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState<AppProfile | null>(null);
    const [search, setSearch] = useState("");
    const [actionFilter, setActionFilter] = useState("all");
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [sort, setSort] = useState<{ key: string; dir: "asc" | "desc" }>({ key: "name", dir: "asc" });

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const res = await baseapi.get("/firewall_sections", {
                params: {
                    query: JSON.stringify({
                        ...(asset_id && client_id ? { asset_id, client_id } : {}),
                        section_name: "application_control_profiles",
                    }),
                    limit: 1,
                },
            });
            if (res.data.status && res.data.data?.[0]) {
                const raw = res.data.data[0].results;
                const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
                setProfiles(parsed.items ?? []);
            } else showError("Failed to load application control profiles");
        } catch (e: any) { showError(e.message); }
        finally { setLoading(false); }
    }, [asset_id, client_id]);

    useEffect(() => { fetchData(); }, [fetchData]);
    useEffect(() => { setPage(1); }, [search, actionFilter]);

    const allActions = Array.from(new Set(
        profiles.flatMap(p => p.entries.map(e => e.action)).filter(Boolean)
    )).sort();

    const filtered = profiles.filter(p => {
        if (actionFilter !== "all" && !p.entries.some(e => e.action === actionFilter)) return false;
        if (search) {
            const q = search.toLowerCase();
            return p.name.toLowerCase().includes(q) || p.comment?.toLowerCase().includes(q);
        }
        return true;
    });

    const sorted = [...filtered].sort((a, b) => {
        let av: any, bv: any;
        if (sort.key === "_entryCount") { av = a.entries.length; bv = b.entries.length; }
        else { av = (a as any)[sort.key]; bv = (b as any)[sort.key]; }
        const cmp = typeof av === "number" && typeof bv === "number"
            ? av - bv
            : String(av ?? "").localeCompare(String(bv ?? ""));
        return sort.dir === "asc" ? cmp : -cmp;
    });

    const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
    const safePage = Math.min(page, totalPages);
    const pageItems = sorted.slice((safePage - 1) * pageSize, safePage * pageSize);
    const start = sorted.length === 0 ? 0 : (safePage - 1) * pageSize + 1;
    const end = Math.min(safePage * pageSize, sorted.length);

    const handleSort = (key: string) => {
        setSort(prev => prev.key === key ? { key, dir: prev.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" });
        setPage(1);
    };

    // ── Row click → replace table with detail view ────────────────────────────
    if (selected) return <ProfileDetail profile={selected} onBack={() => setSelected(null)} />;

    return (
        <div className="flex flex-col gap-3">

            {/* Filters */}
            <div className="flex items-center gap-2 flex-wrap">
                <div className="relative group">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400 dark:text-gray-400 group-focus-within:text-violet-600 dark:group-focus-within:text-violet-400 transition-colors" />
                    <input
                        type="text"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        placeholder="Search profile name..."
                        className="w-56 bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-700 rounded-lg pl-7 pr-3 py-1.5 text-[11px] text-slate-800 dark:text-white placeholder-slate-400 dark:placeholder-gray-600 focus:outline-none focus:border-violet-500/50 transition-all shadow-sm dark:shadow-none"
                    />
                </div>
                <div className="flex items-center gap-1 bg-slate-100 dark:bg-gray-800/60 border border-slate-200 dark:border-gray-700 rounded-lg p-0.5">
                    <button
                        onClick={() => setActionFilter("all")}
                        className={`px-2.5 py-1 rounded-md text-[9px] font-black uppercase tracking-widest transition-all ${actionFilter === "all" ? "bg-white dark:bg-gray-700 text-slate-900 dark:text-white shadow-sm dark:shadow-none" : "text-slate-500 dark:text-gray-400 hover:text-slate-800 dark:hover:text-gray-200"}`}
                    >
                        All
                    </button>
                    {allActions.map(a => (
                        <button
                            key={a}
                            onClick={() => setActionFilter(a)}
                            className={`px-2.5 py-1 rounded-md text-[9px] font-black uppercase tracking-widest transition-all ${actionFilter === a ? "bg-white dark:bg-gray-700 text-slate-900 dark:text-white shadow-sm dark:shadow-none" : "text-slate-500 dark:text-gray-400 hover:text-slate-800 dark:hover:text-gray-200"}`}
                        >
                            {a}
                        </button>
                    ))}
                </div>
                {(search || actionFilter !== "all") && (
                    <button onClick={() => { setSearch(""); setActionFilter("all"); }}
                        className="text-[9px] text-violet-600 dark:text-violet-400 hover:text-violet-700 dark:hover:text-violet-300 font-bold uppercase tracking-widest">
                        Clear
                    </button>
                )}
                <button
                    onClick={fetchData}
                    disabled={loading}
                    className="ml-auto p-1.5 bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-lg text-slate-600 dark:text-gray-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-50 dark:hover:bg-gray-700 transition-all group shadow-sm dark:shadow-none"
                >
                    <RefreshCw size={14} className={loading ? "animate-spin" : "group-hover:text-violet-600 dark:group-hover:text-violet-400"} />
                </button>
            </div>

            {/* Table */}
            {loading ? (
                <div className="flex items-center justify-center gap-2 py-20">
                    <Loader2 className="w-4 h-4 animate-spin text-violet-600 dark:text-violet-400" />
                    <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500 dark:text-gray-400">Loading profiles...</span>
                </div>
            ) : sorted.length === 0 ? (
                <div className="flex flex-col items-center gap-2 py-16 opacity-40">
                    <AppWindow size={22} className="text-slate-400 dark:text-gray-400" />
                    <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500 dark:text-gray-400">No profiles found</span>
                </div>
            ) : (
                <div className="bg-white dark:bg-gray-800/40 border border-slate-200 dark:border-gray-700 rounded-xl overflow-hidden shadow-sm dark:shadow-none">
                    <div className="overflow-x-auto custom-scroll">
                        <table className="w-full text-left border-collapse">
                            <thead className="bg-slate-50 dark:bg-gray-800/80 sticky top-0 z-10">
                                <tr className="border-b border-slate-200 dark:border-gray-700/60">
                                    {PROFILE_COLS.map(col => (
                                        <th
                                            key={col.key}
                                            onClick={() => handleSort(col.key)}
                                            className={`px-3 py-2 text-[9px] font-bold text-slate-700 dark:text-gray-200 uppercase tracking-widest whitespace-nowrap cursor-pointer group select-none ${col.width}`}
                                        >
                                            <div className="flex items-center gap-1">
                                                {col.label}
                                                {sort.key === col.key
                                                    ? sort.dir === "asc"
                                                        ? <ArrowUp size={9} className="text-violet-600 dark:text-violet-400" />
                                                        : <ArrowDown size={9} className="text-violet-600 dark:text-violet-400" />
                                                    : <ArrowUp size={9} className="opacity-0 group-hover:opacity-30 text-slate-400 dark:text-gray-400 transition-opacity" />
                                                }
                                            </div>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-gray-700/30">
                                {pageItems.map(profile => (
                                    <tr
                                        key={profile.name}
                                        onClick={() => setSelected(profile)}
                                        className="hover:bg-slate-50 dark:hover:bg-gray-700/30 transition-colors cursor-pointer"
                                        title="Click to view entries"
                                    >
                                        {PROFILE_COLS.map(col => (
                                            <td key={col.key} className="px-3 py-2.5 align-middle">
                                                {col.render(profile)}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination Footer */}
                    <div className="flex items-center justify-between px-3 py-2 border-t border-slate-200 dark:border-gray-700/60 bg-slate-50 dark:bg-gray-800/40 flex-wrap gap-2">
                        <div className="flex items-center gap-3">
                            <div className="flex items-center gap-1.5">
                                <span className="text-[9px] text-slate-500 dark:text-gray-500 uppercase tracking-widest">Rows</span>
                                <select
                                    value={pageSize}
                                    onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
                                    className="bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-700 rounded px-1.5 py-0.5 text-[10px] text-slate-800 dark:text-gray-300 focus:outline-none shadow-sm dark:shadow-none"
                                >
                                    {PAGE_SIZE_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
                                </select>
                            </div>
                            <span className="text-[9px] text-slate-500 dark:text-gray-400">
                                <span className="text-slate-700 dark:text-gray-300 font-semibold">{start}</span>{" – "}<span className="text-slate-700 dark:text-gray-300 font-semibold">{end}</span>
                                <span className="mx-1 text-slate-400 dark:text-gray-600">/</span>
                                <span className="text-slate-700 dark:text-gray-300 font-bold">{sorted.length}</span>
                                {sorted.length !== profiles.length && (
                                    <span className="text-slate-400 dark:text-gray-500 ml-1">(filtered from {profiles.length})</span>
                                )}
                            </span>
                        </div>
                        <div className="flex items-center gap-1">
                            <button disabled={safePage === 1} onClick={() => setPage(1)}
                                className="p-1 text-slate-500 dark:text-gray-400 hover:text-slate-800 dark:hover:text-white disabled:opacity-20 bg-white dark:bg-gray-800/50 hover:bg-slate-100 dark:hover:bg-gray-700 rounded border border-slate-200 dark:border-transparent transition-all">
                                <ChevronsLeft size={13} />
                            </button>
                            <button disabled={safePage === 1} onClick={() => setPage(p => p - 1)}
                                className="p-1 text-slate-500 dark:text-gray-400 hover:text-slate-800 dark:hover:text-white disabled:opacity-20 bg-white dark:bg-gray-800/50 hover:bg-slate-100 dark:hover:bg-gray-700 rounded border border-slate-200 dark:border-transparent transition-all">
                                <ChevronLeft size={13} />
                            </button>
                            <span className="px-2 py-0.5 bg-violet-500/10 border border-violet-500/20 text-violet-600 dark:text-violet-400 rounded text-[10px] font-bold mx-1 min-w-[28px] text-center">
                                {safePage}
                            </span>
                            <span className="text-[9px] text-slate-500 dark:text-gray-500">/ {totalPages}</span>
                            <button disabled={safePage >= totalPages} onClick={() => setPage(p => p + 1)}
                                className="p-1 text-slate-500 dark:text-gray-400 hover:text-slate-800 dark:hover:text-white disabled:opacity-20 bg-white dark:bg-gray-800/50 hover:bg-slate-100 dark:hover:bg-gray-700 rounded border border-slate-200 dark:border-transparent transition-all">
                                <ChevronRight size={13} />
                            </button>
                            <button disabled={safePage >= totalPages} onClick={() => setPage(totalPages)}
                                className="p-1 text-slate-500 dark:text-gray-400 hover:text-slate-800 dark:hover:text-white disabled:opacity-20 bg-white dark:bg-gray-800/50 hover:bg-slate-100 dark:hover:bg-gray-700 rounded border border-slate-200 dark:border-transparent transition-all">
                                <ChevronsRight size={13} />
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
